import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react'
import MaterialTable, { MTableHeader } from 'material-table'
import { useStyles } from './CustomTable.styles'
import { Autocomplete } from '../Autocomplete'
import { TextField, Tooltip } from '@material-ui/core'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore'
import FirstPageIcon from '@material-ui/icons/FirstPage'
import LastPageIcon from '@material-ui/icons/LastPage'
import FilterListIcon from '@material-ui/icons/FilterList'

export const CustomTable = forwardRef((props, ref) => {
  const {
    components,
    localization,
    options,
    icons,
    searchPlaceholder = 'Buscar...',
    emptyDataSourceMessage = 'Nenhum registro encontrado',
    title = '',
    ...rest
  } = props
  const classes = useStyles()
  const columns = rest.columns || []
  const tableRef = useRef(null)
  const previousColumns = useRef([])
  const [activeFilterColumn, setActiveFilterColumn] = useState(null)
  const onFilterChanged = useRef(null)

  const columnsWithPersistedFilters = useMemo(() => {
    const previousColumnsByKey = new Map(
      previousColumns.current.map((column, index) => [
        column.field || column.title || index,
        column,
      ])
    )

    const nextColumns = columns.map((column, index) => {
      const previousColumn = previousColumnsByKey.get(column.field || column.title || index)
      const previousFilterValue = previousColumn?.tableData?.filterValue

      if (previousFilterValue === undefined) {
        return column
      }

      return {
        ...column,
        tableData: {
          ...column.tableData,
          filterValue: previousFilterValue,
        },
      }
    })

    previousColumns.current = nextColumns
    return nextColumns
  }, [columns])

  useImperativeHandle(ref, () => ({
    filterColumn: (columnId, value) => {
      const column = tableRef.current?.state?.columns?.[columnId]
        || columnsWithPersistedFilters[columnId]
      const currentFilterValue = Array.isArray(column?.tableData?.filterValue)
        ? column.tableData.filterValue[0]
        : column?.tableData?.filterValue
      const nextFilterValue = currentFilterValue === value ? undefined : value

      setActiveFilterColumn(columnId)
      onFilterChanged.current?.(columnId, nextFilterValue)
    },
    getColumns: () => tableRef.current?.state?.columns || columnsWithPersistedFilters,
  }), [columnsWithPersistedFilters])


  const defaultLocalization = {
    pagination: {
      labelDisplayedRows: '{from}-{to} de {count}',
      labelRowsSelect: 'linhas',
      nextTooltip: 'Página seguinte',
      previousTooltip: 'Página anterior',
      lastTooltip: 'Última página',
      firstTooltip: 'Primeira página',
    },
    toolbar: {
      nRowsSelected: '{0} linha(s) selecionadas',
      searchPlaceholder: searchPlaceholder,
      ...localization?.toolbar,
    },
    body: {
      emptyDataSourceMessage: emptyDataSourceMessage,
      ...localization?.body,
    },
    header: {
      actions: 'Ações',
      ...localization?.header,
    },
    ...localization,
  }


  const defaultOptions = {
    search: true,
    pageSize: 10,
    elevation: 0,
    actionsColumnIndex: -1,
    headerStyle: {
      backgroundColor: 'transparent',
    },
    searchFieldStyle: {
      fontSize: '0.875rem',
      backgroundColor: 'transparent',
    },
    ...options,
  }

  const Header = (headerProps) => {
    const columns = headerProps.columns.map((column) => {
      const isFilterable = defaultOptions.filtering && column.filtering !== false
      const isActive = column.tableData.id === activeFilterColumn
      const lookupOptions = column.lookup
        ? Object.keys(column.lookup).map((value) => ({
          value,
          label: column.lookup[value],
        }))
        : []
      const filterValue = Array.isArray(column.tableData.filterValue)
        ? column.tableData.filterValue[0]
        : column.tableData.filterValue
      const selectedLookupOption = lookupOptions.find((option) => option.value === String(filterValue))

      if (!isFilterable) {
        return column
      }

      return {
        ...column,
        title: (
          <span
            className={classes.filterableColumnHeader}
            onClick={(event) => {
              event.stopPropagation()
              if (!isActive) {
                setActiveFilterColumn(column.tableData.id)
              }
            }}
            style={{ display: 'block', width: '100%' }}
          >
            {isActive ? (
              column.filterComponent ? (
                React.createElement(column.filterComponent, {
                  columnDef: column,
                  onFilterChanged: onFilterChanged.current,
                })
              ) : (
                <Autocomplete
                  autoFocus
                  fullWidth
                  multiple={false}
                  disableCloseOnSelect={false}
                  freeSolo={!column.lookup}
                  value={column.lookup ? selectedLookupOption || null : column.tableData.filterValue || ''}
                  options={lookupOptions}
                  label={column.filterPlaceholder || column.title}
                  getOptionLabel={(option) => column.lookup ? option.label || '' : option || ''}
                  getOptionSelected={(option, value) => column.lookup
                    ? option.value === value.value
                    : option === value}
                  onChange={(_, selectedOption) => {
                    if (column.lookup) {
                      onFilterChanged.current?.(column.tableData.id, selectedOption?.value || '')
                    }
                  }}
                  onInputChange={(_, value, reason) => {
                    if (!column.lookup && ['input', 'clear'].includes(reason)) {
                      onFilterChanged.current?.(column.tableData.id, value)
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} variant="standard" InputLabelProps={{ shrink: true }} label={column.filterPlaceholder || column.title} />
                  )}
                />
              )
            ) : (
              <Tooltip title="Clique para filtrar esta coluna" placement="top" arrow>
                <span>{column.title}</span>
              </Tooltip>
            )}
          </span>
        ),
      }
    })

    return <MTableHeader {...headerProps} columns={columns} draggable={false} />
  }

  const defaultComponents = useMemo(() => ({
    Container: forwardRef((propsContainer, containerRef) => (
      <div {...propsContainer} ref={containerRef} style={{ boxShadow: 'none', backgroundColor: 'transparent' }} />
    )),
    Header,
    FilterRow: (filterProps) => {
      onFilterChanged.current = filterProps.onFilterChanged
      return null
    },
    ...components,
  }), [components, activeFilterColumn])

  const defaultIcons = useMemo(() => ({
    SortArrow: forwardRef((iconProps, iconRef) => <ArrowDownwardIcon {...iconProps} ref={iconRef} />),
    Search: forwardRef((iconProps, iconRef) => <SearchIcon {...iconProps} ref={iconRef} />),
    ResetSearch: forwardRef((iconProps, iconRef) => <ClearIcon {...iconProps} ref={iconRef} />),
    NextPage: forwardRef((iconProps, iconRef) => <NavigateNextIcon {...iconProps} ref={iconRef} />),
    PreviousPage: forwardRef((iconProps, iconRef) => <NavigateBeforeIcon {...iconProps} ref={iconRef} />),
    FirstPage: forwardRef((iconProps, iconRef) => <FirstPageIcon {...iconProps} ref={iconRef} />),
    LastPage: forwardRef((iconProps, iconRef) => <LastPageIcon {...iconProps} ref={iconRef} />),
    DetailPanel: forwardRef((iconProps, iconRef) => <NavigateNextIcon {...iconProps} ref={iconRef} />),
    Filter: forwardRef((iconProps, iconRef) => <FilterListIcon {...iconProps} ref={iconRef} />),
    ...icons,
  }), [icons])

  return (
    <div className={classes.tableContainer}>
      <MaterialTable
        ref={tableRef}
        title={title}
        components={defaultComponents}
        localization={defaultLocalization}
        options={defaultOptions}
        icons={defaultIcons}
        {...rest}
        columns={columnsWithPersistedFilters}
      />
    </div>
  )
})
